import { type Action } from 'actions/index';
import { type Draft, type Immutable, produce } from 'immer';
import type * as redux from 'redux';

/**
 * Make a type-safe reducer using `immer`.
 *
 * This will make a reducer where all payloads are fully type-safe, and will use
 * immer to ensure the resulting state is immutable.
 *
 * If you're having problems because your actions are not recognized by typescript,
 * make sure you add type definitions for your actions to the `Action` union
 * type in "actions/index".
 */
export function makeImmerReducer<S, A extends redux.Action<any> = Action>(
  initialState: Draft<S>,
  reducerFn: (draft: Draft<S>, action: A) => Immutable<S> | void,
): redux.Reducer<Immutable<S>, A> {
  const reducer = produce((draft: Draft<S>, action: A) => {
    return reducerFn(draft, action);
  }, initialState);

  return reducer as redux.Reducer<Immutable<S>, A>;
}
